import React, { useContext, useState } from "react";
import { Button, Card, Form, Input, Typography } from "antd";
import { EyeInvisibleOutlined, EyeTwoTone, LockOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Base64 } from "js-base64";
import PasswordValidator from "password-validator";
import AuthContext from "../../context/AuthContext";
import { useRequest } from "../../hooks";

const { Text } = Typography;

const passwordSchema = new PasswordValidator();

passwordSchema
  .is()
  .min(8)
  .is()
  .max(64)
  .has()
  .not()
  .spaces()
  .has()
  .uppercase()
  .has()
  .lowercase()
  .has()
  .digits();

const passwordErrorsMessages = {
  min: "Password should be at least 8 characters long",
  max: "Password should be a maximum of 64 characters long",
  uppercase: "Password should have uppercase characters",
  lowercase: "Password should have lowercase characters",
  digits: "Password should contain digits",
  spaces: "Password should not contain spaces",
  blank: "Password should not be blank",
};

export const ResetPasswordPage = () => {
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleRequest } = useRequest();
  const { handleSuccess, handleWarning } = useContext(AuthContext);
  const [submitting, setSubmitting] = useState(false);
  const authToken = searchParams.get("auth") || "";

  const validatePassword = (password: string) => {
    if (!password) {
      return passwordErrorsMessages.blank;
    }
    const passwordErrors = passwordSchema.validate(password, { list: true });
    if (Array.isArray(passwordErrors) && passwordErrors.length) {
      return passwordErrors
        .map(
          (error) =>
            passwordErrorsMessages[
              error as keyof typeof passwordErrorsMessages
            ],
        )
        .join(", ");
    }
    return "";
  };

  const onFinish = ({ password }: { password: string }) => {
    if (!authToken) {
      handleWarning(true, "The reset password link is invalid or expired.");
      return;
    }
    const validationMessage = validatePassword(password);
    if (validationMessage) {
      handleWarning(true, validationMessage);
      return;
    }
    setSubmitting(true);
    const newPassword = Base64.encode(Base64.encode(password));
    handleRequest({
      endpoint: "reset-password",
      options: {
        method: "PUT",
        body: JSON.stringify({
          token: authToken,
          newPassword,
        }),
      },
      onSuccess: () => {
        setSubmitting(false);
        handleSuccess(true, "Password Updated");
        navigate("/login");
      },
      onError: (e) => {
        setSubmitting(false);
        handleWarning(
          true,
          e?.message || "The reset password link is invalid or expired.",
        );
      },
    });
  };

  return (
    <div className="general-view">
      <section className="login-page" aria-label="Reset password">
        <main className="login-page__form-column">
          <Card
            bordered={false}
            style={{ width: "100%", maxWidth: 440, margin: "0 auto" }}
          >
            <div style={{ marginBottom: 24 }}>
              <LockOutlined style={{ color: "#2276d2", fontSize: 28 }} />
              <h1 style={{ margin: "16px 0 8px" }}>Reset Password</h1>
              <Text type="secondary">
                Enter a new password for your CollectPRO account.
              </Text>
            </div>
            <Form layout="vertical" form={form} onFinish={onFinish}>
              <Form.Item
                label="New password"
                name="password"
                rules={[
                  { required: true, message: "Please input your password" },
                  () => ({
                    validator(_, value) {
                      const validationMessage = validatePassword(value);
                      if (!validationMessage) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(validationMessage));
                    },
                  }),
                ]}
              >
                <Input.Password
                  placeholder="Enter new password"
                  iconRender={(visible) =>
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                />
              </Form.Item>
              <Form.Item
                label="Confirm password"
                name="confirmPassword"
                dependencies={["password"]}
                rules={[
                  { required: true, message: "Please confirm your password" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error("Passwords do not match"));
                    },
                  }),
                ]}
              >
                <Input.Password
                  placeholder="Confirm new password"
                  iconRender={(visible) =>
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                />
              </Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                block
              >
                Reset Password
              </Button>
            </Form>
          </Card>
        </main>
      </section>
    </div>
  );
};
